/**
 * SFE Dashboard cascading-filter model (Requirements 8.1-8.9).
 *
 * `SfeDashboard.jsx` drives seven filters (fiscal year → months → divisions → states →
 * brands → BDM → ZM) with seven `useEffect` steps, each one re-deriving which parent
 * values to send and which children to clear. Those effects have drifted: the states
 * effect reads `selFy`/`selMo` while depending only on `selDiv`, and there is no
 * request cancellation, so a slow response for a stale parent can land after the user
 * has moved on.
 *
 * This module holds that logic as data plus pure functions: the cascade order, each
 * level's declared ancestors, comma-separated serialization (Req 8.8), and a reducer
 * whose only side effect is returning a new state object. Descendant invalidation
 * (Req 8.9) and the latest-request-wins rule live in one place, which makes them
 * property-testable without a network or a render.
 *
 * Nothing here performs I/O, reads the environment, or imports React.
 */

/**
 * Filter levels in fetch order (Req 8.1).
 *
 * Position in this array defines ancestry for `descendantsOf`: everything after a
 * level is downstream of it.
 *
 * @type {readonly string[]}
 */
export const CASCADE_ORDER = Object.freeze([
  'fiscalYear',
  'months',
  'divisions',
  'states',
  'brands',
  'bdm',
  'zm',
]);

/**
 * Ancestors whose selections each level's options request must carry (Req 8.2-8.7).
 *
 * These are the *declared* dependencies, not simply "every earlier level": `bdm` and
 * `zm` are filtered by months onward and deliberately omit `fiscalYear`, matching the
 * `sfe-dashboard/bdm-list` and `sfe-dashboard/zm-list` signatures. `fiscalYear` has no
 * entry because it depends on nothing.
 *
 * @type {Readonly<Record<string, readonly string[]>>}
 */
export const DEPENDENCIES = Object.freeze({
  months: Object.freeze(['fiscalYear']),
  divisions: Object.freeze(['fiscalYear', 'months']),
  states: Object.freeze(['fiscalYear', 'months', 'divisions']),
  brands: Object.freeze(['fiscalYear', 'months', 'divisions', 'states']),
  bdm: Object.freeze(['months', 'divisions', 'states', 'brands']),
  zm: Object.freeze(['months', 'divisions', 'states', 'brands', 'bdm']),
});

/**
 * Cascade level -> backend query-parameter name.
 *
 * The single source of truth shared by `requestParamsFor`, `hooks/useSfeFilters.js`,
 * and the property test. `api/sfeApi.js` speaks the backend's snake_case names, so
 * `requestParamsFor` emits those directly and its result can be spread into a fetcher
 * without a second translation step. Note `months` maps to `yearmonth` and the
 * plural UI levels map to singular backend names (`divisions` → `division`).
 *
 * @type {Readonly<Record<string, string>>}
 */
export const PARAM_NAMES = Object.freeze({
  fiscalYear: 'fiscal_year',
  months: 'yearmonth',
  divisions: 'division',
  states: 'state',
  brands: 'brand',
  bdm: 'bdm',
  zm: 'zm',
});

/** True when `level` is one of the seven known cascade levels. */
function isLevel(level) {
  return typeof level === 'string' && CASCADE_ORDER.includes(level);
}

/**
 * Levels downstream of `level`, in cascade order.
 *
 * The last level (`zm`) and any unknown level yield `[]`, so callers can iterate
 * unconditionally.
 *
 * @param {string} level
 * @returns {string[]} a new array; `CASCADE_ORDER` is never exposed for mutation
 */
export function descendantsOf(level) {
  const index = CASCADE_ORDER.indexOf(level);
  if (index === -1) return [];
  return CASCADE_ORDER.slice(index + 1);
}

/** Coerces one selection entry to a string; unusable entries → null. */
function toSelectionValue(entry) {
  if (typeof entry === 'string') return entry === '' ? null : entry;
  if (typeof entry === 'number' && Number.isFinite(entry)) return String(entry);
  return null;
}

/**
 * Normalizes any selection input to a `string[]`.
 *
 * Accepts an array (entries coerced, unusable ones dropped), an already-serialized
 * comma-separated string, or a finite number. Anything else (`null`, an object, a
 * boolean) yields `[]` rather than throwing, matching how the rest of this codebase
 * treats malformed payloads.
 */
function toSelectionArray(values) {
  if (Array.isArray(values)) {
    return values.reduce((list, entry) => {
      const value = toSelectionValue(entry);
      if (value !== null) list.push(value);
      return list;
    }, []);
  }
  if (typeof values === 'string') return parseMultiSelect(values);
  const single = toSelectionValue(values);
  return single === null ? [] : [single];
}

/**
 * Serializes a multi-select value for the wire (Req 8.8).
 *
 * `['A','B']` → `'A,B'`; `[]` → `''`. Empty-string entries are dropped because they
 * are not selectable options and would otherwise round-trip as phantom values. Values
 * are not trimmed: whatever the backend sent as an option id is what goes back.
 *
 * @param {string[]|string|number|null|undefined} values
 * @returns {string} comma-separated, never `undefined`
 */
export function serializeMultiSelect(values) {
  if (typeof values === 'string') return values;
  return toSelectionArray(values).join(',');
}

/**
 * Inverse of `serializeMultiSelect` (Req 8.8).
 *
 * `'A,B'` → `['A','B']`; `''` → `[]`. Only exactly-empty segments are discarded, so
 * any array of non-empty comma-free values survives a serialize/parse round trip
 * unchanged (Property 23).
 *
 * @param {string|string[]|null|undefined} value
 * @returns {string[]} a new array
 */
export function parseMultiSelect(value) {
  if (Array.isArray(value)) return toSelectionArray(value);
  if (typeof value === 'number' && Number.isFinite(value)) return [String(value)];
  if (typeof value !== 'string' || value === '') return [];
  return value.split(',').filter((part) => part !== '');
}

/**
 * Query parameters for fetching one level's options (Req 8.1-8.7).
 *
 * Emits exactly the level's declared ancestors from `DEPENDENCIES`, keyed by backend
 * parameter name (`PARAM_NAMES`) and serialized with `serializeMultiSelect`. Nothing
 * belonging to the level itself or to its descendants is included, so the parameter
 * set is derived from the dependency table rather than restated per effect
 * (Property 22). `fiscalYear` and unknown levels yield `{}`.
 *
 * Ancestors with no selection are still present, as the empty string — the backend
 * treats an empty filter as "unfiltered", and omitting the key would make "no
 * selection" indistinguishable from "not a dependency".
 *
 * @param {string} level
 * @param {Record<string, string[]>} [selections] typically `state.selections`
 * @returns {Record<string, string>} backend-ready params; a new object every call
 */
export function requestParamsFor(level, selections) {
  const ancestors = DEPENDENCIES[level];
  if (!isLevel(level) || !Array.isArray(ancestors)) return {};

  const source = selections !== null && typeof selections === 'object' ? selections : {};

  return ancestors.reduce((params, ancestor) => {
    params[PARAM_NAMES[ancestor]] = serializeMultiSelect(source[ancestor]);
    return params;
  }, {});
}

/** Builds one frozen record with the same value for every cascade level. */
function levelRecord(makeValue) {
  return Object.freeze(
    CASCADE_ORDER.reduce((record, level) => {
      record[level] = makeValue(level);
      return record;
    }, {}),
  );
}

/**
 * Starting cascade state: nothing selected, no options, nothing loading.
 *
 * Frozen, and reused as the `RESET` result. The reducer never mutates it.
 *
 * @type {Readonly<{
 *   selections: Record<string, string[]>,
 *   options: Record<string, { id: string, label: string }[]>,
 *   loading: Record<string, boolean>,
 *   errors: Record<string, string|null>,
 *   requestId: Record<string, number>,
 * }>}
 */
export const initialCascadeState = Object.freeze({
  selections: levelRecord(() => Object.freeze([])),
  options: levelRecord(() => Object.freeze([])),
  loading: levelRecord(() => false),
  errors: levelRecord(() => null),
  requestId: levelRecord(() => 0),
});

/** Normalizes one option entry to `{ id, label }`; unusable entries → null. */
function toOption(entry) {
  if (typeof entry === 'string' || typeof entry === 'number') {
    const id = toSelectionValue(entry);
    return id === null ? null : { id, label: id };
  }
  if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return null;

  const id = toSelectionValue(entry.id) ?? toSelectionValue(entry.value) ?? toSelectionValue(entry.label);
  if (id === null) return null;
  const label = toSelectionValue(entry.label) ?? toSelectionValue(entry.name) ?? id;
  return { id, label };
}

/**
 * Normalizes an options payload to `{ id, label }[]`.
 *
 * The cascade endpoints return plain strings (`["FY24"]`) as well as objects, so the
 * reducer stores one shape and `MultiSelect` reads `opt.id` / `opt.label` without a
 * branch. A non-array payload yields `[]`.
 */
function toOptions(payload) {
  if (!Array.isArray(payload)) return [];
  return payload.reduce((options, entry) => {
    const option = toOption(entry);
    if (option !== null) options.push(option);
    return options;
  }, []);
}

/** Shallow-copies a level record with one key replaced. */
function withLevel(record, level, value) {
  return { ...record, [level]: value };
}

/** Error text for `OPTIONS_FAILED`, sanitized to a displayable string. */
function toErrorMessage(error) {
  if (typeof error === 'string' && error.trim() !== '') return error;
  if (error && typeof error === 'object' && typeof error.message === 'string' && error.message.trim() !== '') {
    return error.message;
  }
  return 'Unable to load filter options.';
}

/**
 * True when an options action belongs to the level's current request.
 *
 * Latest request wins: a response whose `requestId` no longer matches has been
 * superseded by a newer selection (Req 8.9) and is dropped. A missing `requestId` is
 * treated as stale rather than as a wildcard, so an untagged response can never
 * repopulate a cleared level.
 */
function isCurrentRequest(state, level, requestId) {
  return typeof requestId === 'number' && requestId === state.requestId[level];
}

/**
 * Reduces one cascade action into a new state (Req 8.9).
 *
 * Actions:
 * - `SELECT { level, values }` — writes `selections[level]`, then clears the
 *   selections and options of every level in `descendantsOf(level)` and bumps each
 *   cleared level's `requestId` so their in-flight responses become stale. The
 *   selected level and all of its ancestors keep their selections and options.
 *   Descendant `loading` / `errors` are also reset, since the requests they described
 *   have just been invalidated — without that, a cleared level would keep rendering a
 *   spinner or a stale error forever.
 * - `OPTIONS_LOADING { level, requestId }`, `OPTIONS_LOADED { level, options, requestId }`,
 *   `OPTIONS_FAILED { level, requestId, error }` — applied only when `requestId`
 *   matches `state.requestId[level]`.
 * - `RESET` — back to `initialCascadeState`.
 *
 * Never mutates `state`. Returns the *same* state object for every no-op — unknown
 * action or level, malformed action, stale `requestId` — so React can skip the
 * re-render.
 *
 * @param {typeof initialCascadeState} state
 * @param {{ type: string, level?: string, values?: string[], options?: unknown, requestId?: number, error?: unknown }} action
 * @returns {typeof initialCascadeState}
 */
export function cascadeReducer(state, action) {
  const current = state !== null && typeof state === 'object' ? state : initialCascadeState;
  if (action === null || typeof action !== 'object') return current;

  const { type, level } = action;

  if (type === 'RESET') return initialCascadeState;
  if (!isLevel(level)) return current;

  switch (type) {
    case 'SELECT': {
      const selections = withLevel(current.selections, level, toSelectionArray(action.values));
      const options = { ...current.options };
      const loading = { ...current.loading };
      const errors = { ...current.errors };
      const requestId = { ...current.requestId };

      descendantsOf(level).forEach((child) => {
        selections[child] = [];
        options[child] = [];
        loading[child] = false;
        errors[child] = null;
        requestId[child] = (current.requestId[child] || 0) + 1;
      });

      return { selections, options, loading, errors, requestId };
    }

    case 'OPTIONS_LOADING': {
      if (!isCurrentRequest(current, level, action.requestId)) return current;
      return {
        ...current,
        loading: withLevel(current.loading, level, true),
        errors: withLevel(current.errors, level, null),
      };
    }

    case 'OPTIONS_LOADED': {
      if (!isCurrentRequest(current, level, action.requestId)) return current;
      return {
        ...current,
        options: withLevel(current.options, level, toOptions(action.options)),
        loading: withLevel(current.loading, level, false),
        errors: withLevel(current.errors, level, null),
      };
    }

    case 'OPTIONS_FAILED': {
      if (!isCurrentRequest(current, level, action.requestId)) return current;
      return {
        ...current,
        options: withLevel(current.options, level, []),
        loading: withLevel(current.loading, level, false),
        errors: withLevel(current.errors, level, toErrorMessage(action.error)),
      };
    }

    default:
      return current;
  }
}
